// De chatlogs van het vlak Kwaliteit (W6 ronde 5; V2 bewaartermijn).
//
// Elke chatvraag wordt bewaard met zijn stappen, bronnen, antwoord en de duim
// van de gebruiker (W6 ronde 1, tabel `gesprekken`). V2: de inhoud van een
// gesprek is ALLEEN hier te zien, voor beheerders, en blijft twaalf maanden
// staan; daarna geeft de server een 404 met een zin, en die zin staat hier.
// Ook de zin over de bewaartermijn komt van de server — dit scherm schrijft hem
// niet na, zodat hij niet uit de pas loopt als de termijn ooit verandert.
//
// Filters: alle · duim omlaag · duim omhoog · met fout — één tegelijk, want de
// doelgroep leest een rij knoppen sneller dan een combinatiefilter — plus een
// persoon. De personen komen uit de lijst zelf (de server stuurt geen aparte
// lijst); wie eenmaal voorbijkwam blijft kiesbaar, anders krimpt de keuzelijst
// tot die ene persoon zodra je hem kiest.
//
// Het detail haalt zijn gesprek zelf op, buiten useResource om: die hook maakt
// van elke 404 "Niet gevonden.", en hier heeft de server een betere zin.

import { useEffect, useId, useRef, useState, type ReactNode } from "react";

import { argTekst, TOOLTAAL } from "../../components/ChatBonnetje";
import { Antwoordtekst } from "../../components/ChatPopup";
import { Card } from "../../components/ui";
import { EmptyReason, LoadError } from "../../components/Empty";
import { api, type GesprekkenParams } from "../../lib/api";
import { useResource } from "../../lib/useResource";
import type { GesprekDetail, GesprekkenResponse, GesprekRegel } from "../../contracts";
import { color, radius, sectionLabel, size, weight } from "../../tokens";
import { aantal, datum, noot } from "./BronnenStukken";
import { DuimIcoon, FoutIcoon, KruisIcoon, SlotIcoon } from "./Iconen";
import { cel, kopCel, Tabel, usd } from "./KostenStukken";
import { invoer, knopLicht } from "./stijl";
import { berichtRol, DUIM_LABEL, duimLabel, FOUT_LABEL, leesbaar, tijdelijk } from "./woorden";

type Filter = "alle" | "neer" | "op" | "fout";

const FILTERS: readonly { key: Filter; label: string }[] = [
  { key: "alle", label: "alle" },
  { key: "neer", label: DUIM_LABEL.neer },
  { key: "op", label: DUIM_LABEL.op },
  { key: "fout", label: FOUT_LABEL.filter },
];

/** Precies de parameters die de server kent (GET /api/beheer/gesprekken). */
const PARAMS: Record<Filter, GesprekkenParams> = {
  alle: {},
  neer: { duim: -1 },
  op: { duim: 1 },
  fout: { fout: true },
};

const KOPPEN = ["Wanneer", "Wie", "Vraag", "Duim · fout", "Stappen · bronnen", "Kosten"] as const;

/** "10 sep. 2026, 14:00" — wanneer een gesprek begon; null als de server niets
 *  (leesbaars) stuurde. */
function tijdstip(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString("nl-NL", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function Chatlogs() {
  const [filter, setFilter] = useState<Filter>("alle");
  const [persoon, setPersoon] = useState("");
  const [open, setOpen] = useState<string | null>(null);
  const [personen, setPersonen] = useState<ReadonlyMap<string, string>>(new Map());
  const knoppen = useRef<Record<string, HTMLButtonElement | null>>({});
  const filterGroep = useRef<HTMLDivElement>(null);
  const persoonId = useId();
  const detailId = useId();

  const gesprekken = useResource<GesprekkenResponse>(
    () => api.gesprekken({ ...PARAMS[filter], ...(persoon ? { gebruiker: persoon } : {}) }),
    [filter, persoon],
  );

  // Wie in een antwoord voorkwam, blijft in de keuzelijst staan.
  useEffect(() => {
    const items = gesprekken.data?.items;
    if (!items) return;
    setPersonen((oud) => {
      let nieuw: Map<string, string> | null = null;
      for (const g of items) {
        const id = g.persoon.id;
        if (!id || oud.has(id)) continue;
        nieuw ??= new Map(oud);
        nieuw.set(id, g.persoon.naam ?? g.persoon.email ?? "naam onbekend");
      }
      return nieuw ?? oud;
    });
  }, [gesprekken.data]);

  const opties = [...personen].sort((a, b) => a[1].localeCompare(b[1], "nl"));

  /** De focus naar het gekozen filter: de plek waar de lijst veranderde. */
  const naarFilter = () => {
    filterGroep.current?.querySelector<HTMLButtonElement>('[aria-pressed="true"]')?.focus();
  };

  const sluit = () => {
    const vorige = open;
    setOpen(null);
    // Terug naar de rij waar het detail vandaan kwam; staat die er niet
    // (meer), dan naar het filter — nooit naar niets.
    const rij = vorige ? knoppen.current[vorige] : null;
    if (rij?.isConnected) rij.focus();
    else naarFilter();
  };

  const d = gesprekken.data;
  const filterAan = filter !== "alle" || persoon !== "";

  // Valt het open gesprek door een filter uit de lijst, dan sluit het detail:
  // het hoort bij een rij die er niet meer staat. Stond de focus in het
  // detail, dan gaat hij naar het gekozen filter; anders viel hij naar de body.
  useEffect(() => {
    if (!open || !d || gesprekken.loading) return;
    if (d.items.some((g) => g.id === open)) return;
    const paneel = document.getElementById(detailId);
    const focusErin = paneel?.contains(document.activeElement) ?? false;
    setOpen(null);
    if (focusErin) naarFilter();
  }, [d, open, gesprekken.loading, detailId]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <p
        role="note"
        data-privacy
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 10,
          margin: 0,
          padding: "10px 14px",
          background: color.surfaceQuote,
          border: `1px solid ${color.line}`,
          borderRadius: radius.block,
          fontSize: size.meta,
          color: color.body,
          lineHeight: 1.55,
        }}
      >
        <span style={{ color: color.soft, marginTop: 2 }}>
          <SlotIcoon />
        </span>
        <span>
          <span>Vertrouwelijk: de inhoud van gesprekken staat alleen hier, voor beheerders. </span>
          {d ? (
            <span>{d.telling.bewaartermijn}</span>
          ) : (
            <EmptyReason reason="de bewaartermijn wordt opgehaald" />
          )}
        </span>
      </p>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 12,
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {/* Filterknoppen in het gesegmenteerde patroon van Beheer ("kies wat
            je ziet"), elk met een icoon naast het woord. */}
        <div
          ref={filterGroep}
          role="group"
          aria-label="Welke gesprekken"
          style={{
            display: "inline-flex",
            flexWrap: "wrap",
            gap: 4,
            background: color.lineFaint,
            borderRadius: radius.input,
            padding: 3,
          }}
        >
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={"view-btn" + (filter === f.key ? " active" : "")}
              aria-pressed={filter === f.key}
              onClick={() => setFilter(f.key)}
              style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
            >
              {f.key === "op" && <DuimIcoon />}
              {f.key === "neer" && <DuimIcoon omlaag />}
              {f.key === "fout" && <FoutIcoon />}
              {f.label}
            </button>
          ))}
        </div>

        <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <label htmlFor={persoonId} style={{ fontSize: size.meta, color: color.soft }}>
            Persoon
          </label>
          <select
            id={persoonId}
            value={persoon}
            onChange={(e) => setPersoon(e.target.value)}
            style={{ ...invoer, width: "auto", minWidth: 200, padding: "7px 10px" }}
          >
            <option value="">iedereen</option>
            {opties.map(([id, naam]) => (
              <option key={id} value={id}>
                {naam}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Het detail staat bóven de lijst: bij honderd rijen zou het eronder
          uit beeld vallen. Een nieuw gesprek = een nieuw paneel. */}
      {open && <GesprekPaneel key={open} id={detailId} gesprekId={open} onSluit={sluit} />}

      {gesprekken.error ? (
        <Card style={{ padding: 18 }}>
          <LoadError reason={gesprekken.error} onRetry={gesprekken.reload} />
        </Card>
      ) : !d ? (
        <Card style={{ padding: 18 }}>
          <EmptyReason reason="de gesprekken worden opgehaald" />
        </Card>
      ) : d.items.length === 0 ? (
        <Card style={{ padding: 18 }}>
          <EmptyReason
            reason={filterAan ? "geen gesprekken bij dit filter" : "er is nog geen gesprek bewaard"}
          />
        </Card>
      ) : (
        <div style={{ opacity: gesprekken.stale ? 0.6 : 1, transition: "opacity 0.15s" }}>
          <Tabel label="Bewaarde gesprekken" minBreedte={860}>
            <thead>
              <tr>
                {KOPPEN.map((k) => (
                  <th key={k} scope="col" style={kopCel(k === "Kosten")}>
                    {k}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {d.items.map((g) => (
                <GesprekRij
                  key={g.id}
                  g={g}
                  open={open === g.id}
                  detailId={detailId}
                  knop={(el) => {
                    knoppen.current[g.id] = el;
                  }}
                  onOpen={() => setOpen(g.id)}
                />
              ))}
            </tbody>
          </Tabel>
        </div>
      )}
      {d && <p style={{ ...noot, marginTop: 0 }}>{d.limietTekst}</p>}
    </div>
  );
}

function GesprekRij({
  g,
  open,
  detailId,
  knop,
  onOpen,
}: {
  g: GesprekRegel;
  open: boolean;
  detailId: string;
  knop: (el: HTMLButtonElement | null) => void;
  onOpen: () => void;
}) {
  return (
    <tr data-gesprek={g.id} style={{ background: open ? color.surfaceQuote : undefined }}>
      <td style={{ ...cel(), whiteSpace: "nowrap" }}>
        {tijdstip(g.gestartOp) ?? <EmptyReason reason="tijdstip onbekend" />}
      </td>
      <td style={cel()}>{g.persoon.naam ?? <EmptyReason reason="onbekende gebruiker" />}</td>
      <td style={{ ...cel(), maxWidth: 340 }}>
        {/* De hele vraag staat in de knop; de kaart kort hem visueel in. */}
        <button
          ref={knop}
          type="button"
          onClick={onOpen}
          aria-expanded={open}
          aria-controls={open ? detailId : undefined}
          title={g.vraag}
          style={{
            display: "block",
            maxWidth: "100%",
            background: "none",
            border: "none",
            padding: 0,
            fontFamily: "inherit",
            fontSize: size.meta,
            fontWeight: weight.medium,
            color: color.ink,
            textAlign: "left",
            cursor: "pointer",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            textDecoration: "underline",
            textDecorationColor: color.border,
            textUnderlineOffset: 3,
          }}
        >
          {g.vraag || <EmptyReason reason="geen vraag bewaard" />}
        </button>
      </td>
      <td style={cel()}>
        <Signalen duim={g.duim} fout={g.fout} />
      </td>
      <td style={{ ...cel(), whiteSpace: "nowrap" }}>
        {`${aantal(g.stappen, "stap", "stappen")} · ${aantal(g.bronnen, "bron", "bronnen")}`}
      </td>
      <td style={cel(true)}>
        {g.kostenUsd !== null ? (
          usd(g.kostenUsd)
        ) : (
          <EmptyReason reason={g.kostenReden ?? "geen bedrag bekend"} />
        )}
      </td>
    </tr>
  );
}

/** De duim (als icoon, met het woord voor wie hem niet ziet) en de foutbadge. */
function Signalen({ duim, fout }: { duim: -1 | 1 | null; fout: string | null }) {
  if (duim === null && !fout) {
    return <span style={{ fontSize: 12, color: color.faint }}>{DUIM_LABEL.geen}</span>;
  }
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
      {duim !== null && <Duim duim={duim} />}
      {fout && (
        <span
          data-fout
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            fontSize: 11,
            fontWeight: weight.medium,
            color: color.accentRed,
            background: color.lineFaint,
            border: `1px solid ${color.line}`,
            borderRadius: radius.pill,
            padding: "2px 8px",
            whiteSpace: "nowrap",
          }}
        >
          <FoutIcoon />
          {FOUT_LABEL.badge}
        </span>
      )}
    </span>
  );
}

function Duim({ duim }: { duim: -1 | 1 }) {
  const woord = duimLabel(duim);
  return (
    <span
      role="img"
      aria-label={woord}
      title={woord}
      style={{
        display: "inline-flex",
        color: duim === 1 ? color.accentGreen : color.accentRed,
      }}
    >
      <DuimIcoon omlaag={duim === -1} />
    </span>
  );
}

// ── Het detail van één gesprek ───────────────────────────────────────────────

type DetailStaat =
  | { soort: "laden" }
  | { soort: "klaar"; data: GesprekDetail }
  | { soort: "fout"; zin: string; opnieuw: boolean };

function GesprekPaneel({
  id,
  gesprekId,
  onSluit,
}: {
  id: string;
  gesprekId: string;
  onSluit: () => void;
}) {
  const [staat, setStaat] = useState<DetailStaat>({ soort: "laden" });
  const [poging, setPoging] = useState(0);
  const kop = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    let levend = true;
    setStaat({ soort: "laden" });
    api.gesprek(gesprekId).then(
      (data) => {
        if (levend) setStaat({ soort: "klaar", data });
      },
      (err: unknown) => {
        if (!levend) return;
        // Bij een 404 (na de bewaartermijn) is de zin van de server de uitleg.
        setStaat({
          soort: "fout",
          zin: leesbaar(err, "Het gesprek kon niet worden opgehaald."),
          opnieuw: tijdelijk(err),
        });
      },
    );
    return () => {
      levend = false;
    };
  }, [gesprekId, poging]);

  // Wie een gesprek opent, leest verder in het paneel.
  useEffect(() => {
    kop.current?.focus();
  }, []);

  const kopTekst =
    staat.soort === "klaar"
      ? `Gesprek van ${staat.data.persoon.naam ?? "een onbekende gebruiker"}${
          tijdstip(staat.data.gestartOp) ? ` · ${tijdstip(staat.data.gestartOp)}` : ""
        }`
      : "Gesprek";

  return (
    <section
      id={id}
      aria-labelledby={`${id}-kop`}
      data-gesprek-detail
      onKeyDown={(e) => {
        if (e.key === "Escape") onSluit();
      }}
    >
      <Card style={{ padding: "16px 18px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <h3
            id={`${id}-kop`}
            ref={kop}
            tabIndex={-1}
            style={{ fontSize: size.body, fontWeight: weight.medium, color: color.ink, outline: "none" }}
          >
            {kopTekst}
          </h3>
          <button
            type="button"
            onClick={onSluit}
            style={{ ...knopLicht, display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <KruisIcoon /> Sluiten
          </button>
        </div>

        {staat.soort === "laden" && (
          <div style={{ marginTop: 10 }}>
            <EmptyReason reason="het gesprek wordt opgehaald" />
          </div>
        )}
        {staat.soort === "fout" && (
          <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
            <p role="alert" style={{ fontSize: size.meta, color: color.accentRed, lineHeight: 1.5 }}>
              {staat.zin}
            </p>
            {staat.opnieuw && (
              <button type="button" onClick={() => setPoging((p) => p + 1)} style={knopLicht}>
                Opnieuw proberen
              </button>
            )}
          </div>
        )}
        {staat.soort === "klaar" && <DetailInhoud d={staat.data} />}
      </Card>
    </section>
  );
}

function DetailInhoud({ d }: { d: GesprekDetail }) {
  const berichten = d.berichten.map(leesBericht);
  const stappen = stappenInWoorden(d.stappenLijst);
  const bronnen = d.bronnenLijst.map(leesBron);
  const bewaarTot = datum(d.bewaarTot);

  return (
    <dl style={{ margin: "12px 0 0", display: "flex", flexDirection: "column", gap: 14 }}>
      <Veld titel="Vraag">
        {d.vraag ? <p style={tekstBlok}>{d.vraag}</p> : <EmptyReason reason="geen vraag bewaard" />}
      </Veld>

      <Veld titel="Berichten">
        {berichten.length > 0 ? (
          <ol style={lijst}>
            {berichten.map((b, i) => (
              <li key={i}>
                <span style={{ fontWeight: weight.medium, color: color.ink }}>
                  {berichtRol(b.rol)}
                </span>
                {": "}
                {b.inhoud && b.rol === "assistant" ? (
                  // Een antwoord zoals de chat het toonde, niet als ruwe markdown.
                  <div data-antwoord style={{ overflowWrap: "anywhere" }}>
                    <Antwoordtekst tekst={b.inhoud} />
                  </div>
                ) : b.inhoud ? (
                  <span style={tekstBlok}>{b.inhoud}</span>
                ) : (
                  <EmptyReason reason="bericht zonder tekst" />
                )}
              </li>
            ))}
          </ol>
        ) : (
          <EmptyReason reason="geen berichten bewaard" />
        )}
      </Veld>

      <Veld titel="Stappen">
        {stappen.length > 0 ? (
          <ol style={lijst} data-stappen>
            {stappen.map((s, i) => (
              <li key={i}>
                {s.bekend ? (
                  <span style={{ color: color.ink }}>{s.tekst}</span>
                ) : (
                  <EmptyReason reason={s.tekst} />
                )}
                {s.arg && <span>{` ${s.arg}`}</span>}
                {s.uitkomst && <span style={{ color: color.soft }}>{` → ${s.uitkomst}`}</span>}
              </li>
            ))}
          </ol>
        ) : (
          <EmptyReason reason="de chat zette geen stappen" />
        )}
      </Veld>

      <Veld titel="Bronnen">
        {bronnen.length > 0 ? (
          <ul style={lijst}>
            {bronnen.map((titel, i) => (
              <li key={i}>{titel ?? <EmptyReason reason="bron zonder titel" />}</li>
            ))}
          </ul>
        ) : (
          <EmptyReason reason="de chat noemde geen bronnen" />
        )}
      </Veld>

      <Veld titel="Antwoord">
        {d.antwoord ? (
          // Zoals de chat het antwoord toonde (kop, opsomming, vet, tabel) —
          // dezelfde renderer, dus nooit ruwe markdown als "**Cloud**".
          <div data-antwoord style={{ overflowWrap: "anywhere" }}>
            <Antwoordtekst tekst={d.antwoord} />
          </div>
        ) : (
          <EmptyReason reason={d.fout ? "geen antwoord: het gesprek mislukte" : "geen antwoord bewaard"} />
        )}
      </Veld>

      {d.fout && (
        <Veld titel="Wat misging">
          <p style={{ color: color.accentRed }}>{d.fout}</p>
        </Veld>
      )}

      <Veld titel="Duim van de gebruiker">
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          {d.duim !== null && <Duim duim={d.duim} />}
          {duimLabel(d.duim)}
        </span>
        {d.opmerking ? (
          <p style={{ ...tekstBlok, marginTop: 4 }}>{`“${d.opmerking}”`}</p>
        ) : (
          d.duim !== null && (
            <div style={{ marginTop: 4 }}>
              <EmptyReason reason="zonder opmerking" />
            </div>
          )
        )}
      </Veld>

      <Veld titel="Model en instellingen">
        {d.model ?? <EmptyReason reason="model niet vastgelegd" />}
        {" · "}
        {d.configVersie ? (
          `instellingen ${d.configVersie}`
        ) : (
          <EmptyReason reason="instellingen niet vastgelegd" />
        )}
      </Veld>

      <Veld titel="Kosten">
        {d.kostenUsd !== null ? (
          usd(d.kostenUsd)
        ) : (
          <EmptyReason reason={d.kostenReden ?? "geen bedrag bekend"} />
        )}
      </Veld>

      <Veld titel="Bewaartermijn">
        {bewaarTot ? `Wordt bewaard tot ${bewaarTot}.` : <EmptyReason reason="bewaartermijn onbekend" />}
      </Veld>
    </dl>
  );
}

function Veld({ titel, children }: { titel: string; children: ReactNode }) {
  return (
    <div>
      <dt style={{ ...sectionLabel, marginBottom: 4 }}>{titel}</dt>
      <dd style={{ margin: 0, fontSize: size.meta, color: color.body, lineHeight: 1.55 }}>
        {children}
      </dd>
    </div>
  );
}

const tekstBlok = { whiteSpace: "pre-wrap", overflowWrap: "anywhere" } as const;

const lijst = {
  margin: 0,
  paddingLeft: 18,
  display: "flex",
  flexDirection: "column",
  gap: 4,
} as const;

// ── De bewaarde onderdelen lezen ─────────────────────────────────────────────
//
// Het contract zegt van berichten, stappen en bronnen alleen dat het objecten
// zijn: de server bewaart ze zoals de chat ze maakte. Dus lezen we voorzichtig
// wat we kennen en tonen we bij de rest de reden, nooit een ruwe dump.

type Los = Record<string, unknown>;

function isLos(v: unknown): v is Los {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function tekstVan(v: unknown): string | null {
  return typeof v === "string" && v.trim() ? v : null;
}

function leesBericht(b: unknown): { rol: string | null; inhoud: string | null } {
  if (!isLos(b)) return { rol: null, inhoud: null };
  return { rol: tekstVan(b["role"]), inhoud: tekstVan(b["content"]) };
}

function leesBron(b: unknown): string | null {
  if (!isLos(b)) return null;
  return tekstVan(b["title"]) ?? tekstVan(b["titel"]);
}

/** Wat er staat bij een tool die TOOLTAAL niet kent: geen ruwe sleutel. */
export const ONBEKENDE_STAP = "een stap die dit scherm nog niet kent";

export type StapRegel = {
  tekst: string;
  /** false = een tool die TOOLTAAL niet kent; de tekst is dan ONBEKENDE_STAP. */
  bekend: boolean;
  arg: string | null;
  uitkomst: string | null;
};

/**
 * De stappen zoals de chat ze bewaarde: "tool_start" en "tool_klaar" van de
 * stroom, of één "tool" per gebruikte tool bij de gewone route (router.py).
 * Een start en zijn klaar worden één regel. De woorden zijn die van het
 * bonnetje onder het chatantwoord (TOOLTAAL, argTekst): de beheerder leest wat
 * de gebruiker zag. Een tool die de lijst niet kent, toont zijn sleutel niet
 * maar gedempt "een stap die dit scherm nog niet kent" — dan hoort hij in TOOLTAAL.
 */
export function stappenInWoorden(lijstIn: readonly unknown[]): StapRegel[] {
  const regels: { tool: string; regel: StapRegel; klaar: boolean }[] = [];
  for (const ruw of lijstIn) {
    if (!isLos(ruw)) continue;
    const tool = tekstVan(ruw["tool"]);
    if (!tool) continue;
    const soort = ruw["soort"];
    const uitkomst = tekstVan(ruw["samenvatting"]);
    if (soort === "tool_klaar") {
      const lopend = regels.find((r) => r.tool === tool && !r.klaar);
      if (lopend) {
        lopend.klaar = true;
        lopend.regel.uitkomst = uitkomst;
        continue;
      }
    }
    const woord = TOOLTAAL[tool];
    regels.push({
      tool,
      klaar: soort !== "tool_start",
      regel: {
        tekst: woord ?? ONBEKENDE_STAP,
        bekend: woord !== undefined,
        arg: argTekst(isLos(ruw["args"]) ? ruw["args"] : null),
        uitkomst: soort === "tool_klaar" ? uitkomst : null,
      },
    });
  }
  return regels.map((r) => r.regel);
}
